import CakeIcon from "@mui/icons-material/Cake";
import { Grid } from "@mui/material";
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
  startOfDay,
} from "date-fns";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllDispatcherAssignmentsQuery } from "../../api/dispatcherAssignmentsApi";
import { useGetExpiringDocumentsQuery } from "../../api/documentsApi";
import {
  useGetDispatchersQuery,
  useGetEmployeeBirthdaysForMonthQuery,
  useGetTruckDriversQuery,
} from "../../api/employeesApi";
import { useGetAllTrailerAssignmentsQuery } from "../../api/trailerAssignmentsApi";
import { useGetTrailersQuery } from "../../api/trailersApi";
import { useGetAllVehicleAssignmentsQuery } from "../../api/vehicleAssignmentsApi";
import { useGetVehiclesQuery } from "../../api/vehiclesApi";
import { useAppSelector } from "../../app/hooks";
import {
  AssignmentIndIcon,
  SafetyBoardIcon,
} from "../../components/common/pageIcons";
import { MenuItemBase } from "../../components/layout/navMenu";
import { UserRoles } from "../../constants/roles";
import { hasSubscriptionAccess } from "../../constants/subscriptionLevels";
import HubGroupPanel, { HubPreviewRow } from "./components/HubGroupPanel";
import HubOverviewSection from "./components/HubOverviewSection";
import { hasRole, hasShortcut } from "./hubUtils";

interface SafetyHubOverviewProps {
  shortcuts: MenuItemBase[];
  subscriptionLevel: string | null;
}

const EXPIRING_ROLES = [UserRoles.ADMIN, UserRoles.SAFETY, UserRoles.IFTA_ADMIN];

const entityTypeLabel = (entityType: string) => {
  if (entityType.toLowerCase() === "employee") return "Driver";
  return entityType;
};

const daysUntil = (isoDate: string) =>
  differenceInCalendarDays(startOfDay(parseISO(isoDate)), startOfDay(new Date()));

const statusFromDays = (days: number) => {
  if (days < 0) return "expired";
  if (days <= 7) return "critical";
  if (days <= 30) return "warning";
  return "valid";
};

const formatExpiryMeta = (days: number) => {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Expires today";
  return `${days}d left`;
};

const SafetyHubOverview: React.FC<SafetyHubOverviewProps> = ({
  shortcuts,
  subscriptionLevel,
}) => {
  const navigate = useNavigate();
  const role = useAppSelector((state) => state.auth.user)?.role;
  const today = useMemo(() => new Date(), []);

  const canExpiring =
    hasShortcut(shortcuts, "/safety") && hasRole(role, EXPIRING_ROLES);
  const canAssignments = hasShortcut(shortcuts, "/admin/driver-assignments");
  const canBirthdays =
    hasRole(role, [UserRoles.ADMIN]) &&
    hasSubscriptionAccess(subscriptionLevel, "Standard");

  const expiredQuery = useGetExpiringDocumentsQuery(
    { pageNumber: 1, pageSize: 1, status: "expired" },
    { skip: !canExpiring },
  );
  const criticalQuery = useGetExpiringDocumentsQuery(
    { pageNumber: 1, pageSize: 1, status: "critical" },
    { skip: !canExpiring },
  );
  const warningQuery = useGetExpiringDocumentsQuery(
    { pageNumber: 1, pageSize: 1, status: "warning" },
    { skip: !canExpiring },
  );
  const soonestQuery = useGetExpiringDocumentsQuery(
    { pageNumber: 1, pageSize: 20 },
    { skip: !canExpiring },
  );

  const { data: drivers = [], isLoading: driversLoading } =
    useGetTruckDriversQuery(undefined, { skip: !canAssignments });
  const { data: dispatchers = [], isLoading: dispatchersLoading } =
    useGetDispatchersQuery(undefined, { skip: !canAssignments });
  const { data: vehicles = [], isLoading: vehiclesLoading } =
    useGetVehiclesQuery(undefined, { skip: !canAssignments });
  const { data: trailers = [], isLoading: trailersLoading } =
    useGetTrailersQuery(undefined, { skip: !canAssignments });
  const { data: vehicleAssignments = [], isLoading: vaLoading } =
    useGetAllVehicleAssignmentsQuery(undefined, { skip: !canAssignments });
  const { data: trailerAssignments = [], isLoading: taLoading } =
    useGetAllTrailerAssignmentsQuery(undefined, { skip: !canAssignments });
  const { data: dispatcherAssignments = [], isLoading: daLoading } =
    useGetAllDispatcherAssignmentsQuery(undefined, { skip: !canAssignments });

  const { data: birthdays = [], isLoading: birthdaysLoading } =
    useGetEmployeeBirthdaysForMonthQuery(
      { year: today.getFullYear(), month: today.getMonth() + 1 },
      { skip: !canBirthdays },
    );

  const expiredCount = expiredQuery.isError
    ? 0
    : expiredQuery.data?.totalCount ?? 0;
  const criticalCount = criticalQuery.isError
    ? 0
    : criticalQuery.data?.totalCount ?? 0;
  const warningCount = warningQuery.isError
    ? 0
    : warningQuery.data?.totalCount ?? 0;

  const documentsHref = expiredCount
    ? "/safety?status=expired"
    : criticalCount
      ? "/safety?status=critical"
      : warningCount
        ? "/safety?status=warning"
        : "/safety";

  const documentRows = useMemo<HubPreviewRow[]>(() => {
    return (soonestQuery.data?.items ?? []).map((group) => {
      const days = daysUntil(group.earliestExpiration);
      const status = statusFromDays(days);
      const extra =
        group.documentCount > 1 ? ` · ${group.documentCount} docs` : "";
      return {
        id: `doc-${group.entityType}-${group.entityId}`,
        title: group.entityName,
        subtitle: `${entityTypeLabel(group.entityType)}${extra}`,
        meta: formatExpiryMeta(days),
        tone: days <= 7 ? "error" : days <= 30 ? "warning" : "neutral",
        onClick: () =>
          navigate(status === "valid" ? "/safety" : `/safety?status=${status}`),
      };
    });
  }, [navigate, soonestQuery.data?.items]);

  const unassigned = useMemo(() => {
    const assignedDriverInfoIds = new Set([
      ...vehicleAssignments.map((a) => a.employeeId),
      ...trailerAssignments.map((a) => a.employeeId),
      ...dispatcherAssignments.map((a) => a.employeeDriverInfoId),
    ]);
    const assignedVehicleIds = new Set(
      vehicleAssignments.map((a) => a.vehicleId),
    );
    const assignedTrailerIds = new Set(
      trailerAssignments.map((a) => a.trailerId),
    );
    const assignedDispatcherIds = new Set(
      dispatcherAssignments.map((a) => a.dispatcherEmployeeId),
    );

    const unassignedDrivers = drivers.filter(
      (d) => !assignedDriverInfoIds.has(d.id),
    );
    const unassignedVehicles = vehicles.filter(
      (v) => !assignedVehicleIds.has(v.id),
    );
    const unassignedTrailers = trailers.filter(
      (t) => !assignedTrailerIds.has(t.id) && !t.isArchived,
    );
    const unassignedDispatchers = dispatchers.filter(
      (d) => !assignedDispatcherIds.has(d.id),
    );

    const rows: HubPreviewRow[] = [
      ...unassignedDrivers.map((driver) => ({
        id: `driver-${driver.id}`,
        title: `${driver.firstName} ${driver.lastName}`.trim(),
        subtitle: "Driver",
        meta: "Unassigned",
        tone: "warning" as const,
        onClick: () => navigate("/admin/driver-assignments"),
      })),
      ...unassignedVehicles.map((vehicle) => ({
        id: `vehicle-${vehicle.id}`,
        title: vehicle.name || `Vehicle #${vehicle.id}`,
        subtitle: "Truck",
        meta: "Unassigned",
        tone: "warning" as const,
        onClick: () => navigate("/admin/driver-assignments"),
      })),
      ...unassignedTrailers.map((trailer) => ({
        id: `trailer-${trailer.id}`,
        title: trailer.unitNumber || `Trailer #${trailer.id}`,
        subtitle: "Trailer",
        meta: "Unassigned",
        tone: "info" as const,
        onClick: () => navigate("/admin/driver-assignments"),
      })),
      ...unassignedDispatchers.map((dispatcher) => ({
        id: `dispatcher-${dispatcher.id}`,
        title: `${dispatcher.firstName} ${dispatcher.lastName}`.trim(),
        subtitle: "Dispatcher",
        meta: "Unassigned",
        tone: "info" as const,
        onClick: () => navigate("/admin/driver-assignments"),
      })),
    ];

    return {
      drivers: unassignedDrivers.length,
      vehicles: unassignedVehicles.length,
      trailers: unassignedTrailers.length,
      dispatchers: unassignedDispatchers.length,
      rows,
    };
  }, [
    dispatcherAssignments,
    dispatchers,
    drivers,
    navigate,
    trailerAssignments,
    trailers,
    vehicleAssignments,
    vehicles,
  ]);

  const birthdayRows = useMemo<HubPreviewRow[]>(() => {
    return [...birthdays]
      .sort((a, b) => a.birthdayDate.localeCompare(b.birthdayDate))
      .map((person) => {
        const birthday = parseISO(person.birthdayDate);
        const isToday = isSameDay(birthday, today);
        return {
          id: `bday-${person.employeeId}`,
          title: person.employeeName,
          subtitle: `Turns ${person.age}`,
          meta: isToday ? "Today" : format(birthday, "MMM d"),
          tone: isToday ? "info" : "neutral",
          onClick: () => navigate("/admin/absences?tab=calendar"),
        };
      });
  }, [birthdays, navigate, today]);

  const assignmentsLoading =
    driversLoading ||
    dispatchersLoading ||
    vehiclesLoading ||
    trailersLoading ||
    vaLoading ||
    taLoading ||
    daLoading;

  const badges: Record<string, number> = {};
  if (canExpiring) badges["/safety"] = expiredCount + criticalCount;
  if (canAssignments) {
    badges["/admin/driver-assignments"] =
      unassigned.drivers +
      unassigned.vehicles +
      unassigned.trailers +
      unassigned.dispatchers;
  }

  return (
    <HubOverviewSection shortcuts={shortcuts} badges={badges}>
      {canExpiring && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<SafetyBoardIcon />}
            title="Expiring documents"
            href={documentsHref}
            hrefLabel="Board"
            loading={
              expiredQuery.isLoading ||
              criticalQuery.isLoading ||
              warningQuery.isLoading ||
              soonestQuery.isLoading
            }
            emptyText="No documents expired or due in the next 30 days."
            counts={[
              { label: "expired", value: expiredCount, tone: "error" },
              { label: "≤7 days", value: criticalCount, tone: "error" },
              { label: "8–30 days", value: warningCount, tone: "warning" },
            ]}
            rows={documentRows}
          />
        </Grid>
      )}
      {canAssignments && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<AssignmentIndIcon />}
            title="Unassigned"
            href="/admin/driver-assignments"
            hrefLabel="Assignments"
            loading={assignmentsLoading}
            emptyText="Every driver, truck, trailer, and dispatcher is assigned."
            counts={[
              { label: "drivers", value: unassigned.drivers, tone: "warning" },
              { label: "trucks", value: unassigned.vehicles, tone: "warning" },
              { label: "trailers", value: unassigned.trailers, tone: "info" },
              {
                label: "dispatchers",
                value: unassigned.dispatchers,
                tone: "info",
              },
            ]}
            rows={unassigned.rows}
          />
        </Grid>
      )}
      {canBirthdays && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<CakeIcon />}
            title="Birthdays this month"
            href="/admin/absences?tab=calendar"
            hrefLabel="Calendar"
            loading={birthdaysLoading}
            emptyText="No employee birthdays this month."
            counts={[
              {
                label: "this month",
                value: birthdays.length,
                tone: "info",
              },
              {
                label: "today",
                value: birthdays.filter((person) =>
                  isSameDay(parseISO(person.birthdayDate), today),
                ).length,
                tone: "info",
              },
            ]}
            rows={birthdayRows}
          />
        </Grid>
      )}
    </HubOverviewSection>
  );
};

export default SafetyHubOverview;
